/**
 * Plan Reasoning Hook
 *
 * Integrates the Spindle WASM engine with plan entries for defeasible reasoning.
 * Provides query functions similar to hence-guile CLI.
 * Uses a Web Worker to avoid blocking the main thread.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSpindleWorker } from "./useSpindleWorker";
import type { Conclusion, MetaEntry } from "./spindle";
import { getCachedReasoning, setCachedReasoning } from "./reasoningCache";

interface LogEntry {
  seq: number;
  ts: string;
  agent: string;
  forms: string[];
  sig: string;
}

interface TaskMeta {
  description?: string;
  priority?: string;
  estimate?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
  note?: string;
  source?: string;
  [key: string]: string | string[] | undefined; // Allow additional meta fields
}

interface Task {
  id: string;
  status: "backlog" | "ready" | "in_progress" | "blocked" | "done";
  conclusion?: "+D" | "+d" | "-D" | "-d";
  agent?: string;
  agentKeyHash?: string;
  agentType?: "C" | "S" | "A";
  progress?: number;
  deps?: string;
  note?: string;
  blockedBy?: string[];
  meta?: TaskMeta;
}

interface Assignment {
  task: string;
  agent: string;
  agentType?: "C" | "S" | "A";
}

type LoadingStage = "idle" | "wasm" | "reasoning" | "done";

interface PlanReasoningResult {
  isLoading: boolean;
  loadingStage: LoadingStage;
  conclusions: Conclusion[];
  tasks: Task[];
  taskDeps: Map<string, string[]>;
  readyTasks: string[];
  completedTasks: string[];
  blockedTasks: string[];
  assignments: Assignment[];
  query: (literal: string) => "+D" | "+d" | "-D" | "-d";
  refresh: () => void;
}

/**
 * Hook for plan-specific reasoning using Spindle WASM (via Web Worker)
 *
 * Caches reasoning results keyed by plan token and max sequence number.
 * Since the log is append-only, we only re-run reasoning when new entries arrive.
 */
export function usePlanReasoning(
  entries: LogEntry[],
  planSource?: string,
  token?: string | null
): PlanReasoningResult {
  const worker = useSpindleWorker();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastTheoryRef = useRef<string>("");
  const lastMaxSeqRef = useRef<number>(0);
  const [cachedConclusions, setCachedConclusions] = useState<Conclusion[] | null>(null);
  const [cachedMeta, setCachedMeta] = useState<MetaEntry[] | null>(null);
  const hasLoadedOnceRef = useRef(false);

  // Build SPL theory from entries (WASM supports SPL directly)
  const buildTheory = useCallback(() => {
    const forms: string[] = [];

    // Add plan source if provided
    if (planSource) {
      forms.push(planSource);
    }

    // Add forms from entries
    for (const entry of entries) {
      for (const form of entry.forms) {
        forms.push(form);
      }
    }

    return forms.join("\n");
  }, [entries, planSource]);

  // Get max sequence number from entries
  const maxSeq = entries.length > 0 ? Math.max(...entries.map(e => e.seq)) : 0;

  // Check cache on maxSeq change (before running expensive reasoning)
  useEffect(() => {
    if (!token || maxSeq === 0 || maxSeq === lastMaxSeqRef.current) return;

    let cancelled = false;

    getCachedReasoning(token, maxSeq).then((cached) => {
      if (cancelled) return;

      if (cached) {
        console.log("[PlanReasoning] Cache hit for maxSeq:", maxSeq);
        setCachedConclusions(cached.conclusions);
        setCachedMeta(cached.meta);
        lastMaxSeqRef.current = maxSeq;
        hasLoadedOnceRef.current = true; // Cache hit counts as first load complete
      } else {
        // Cache miss - clear cached state so we fall through to worker
        setCachedConclusions(null);
        setCachedMeta(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [token, maxSeq]);

  // Memoize theory string to prevent unnecessary re-renders
  const theory = useMemo(() => buildTheory(), [buildTheory]);

  // Run reasoning when worker is ready and theory changes
  useEffect(() => {
    if (!worker.isReady) return;

    // Skip if theory hasn't changed
    if (theory === lastTheoryRef.current) return;
    lastTheoryRef.current = theory;

    worker.reason(theory);
  }, [worker.isReady, worker.reason, theory, refreshTrigger]);

  // Update cache when reasoning completes
  const lastConclusionCountRef = useRef<number>(0);
  useEffect(() => {
    if (worker.conclusions.length > 0 && worker.conclusions.length !== lastConclusionCountRef.current) {
      lastConclusionCountRef.current = worker.conclusions.length;

      // Mark first load complete - subsequent re-reasoning won't show spinner
      hasLoadedOnceRef.current = true;

      // Update cache with new results (fire and forget)
      if (token && maxSeq > 0) {
        setCachedReasoning(token, maxSeq, worker.conclusions, worker.meta);
        lastMaxSeqRef.current = maxSeq;
        setCachedConclusions(null); // Clear so we use worker results directly
        setCachedMeta(null);
      }
    }
  }, [worker.conclusions, worker.meta, token, maxSeq]);

  // Use cached results if available, otherwise worker results
  const effectiveConclusions = cachedConclusions ?? worker.conclusions;
  const effectiveMeta = cachedMeta ?? worker.meta;
  // Only show loading spinner on first load - re-reasoning just transitions the UI
  const isEffectivelyLoading = worker.isLoading && cachedConclusions === null && !hasLoadedOnceRef.current;

  // Extract task dependencies from WASM meta (AST-based) with SPL regex as fallback
  const taskDeps = useMemo(() => {
    if (isEffectivelyLoading) return new Map<string, string[]>();
    const wasmDeps = extractTaskDepsFromMeta(effectiveMeta);
    const splDeps = planSource ? extractTaskDepsFromSpl(planSource) : new Map<string, string[]>();

    // Prefer WASM deps (extracted from parsed AST) over SPL regex-based extraction
    const merged = new Map<string, string[]>(wasmDeps);
    for (const [taskId, deps] of splDeps) {
      if (!merged.has(taskId)) {
        merged.set(taskId, deps);
      }
    }
    return merged;
  }, [isEffectivelyLoading, effectiveMeta, planSource]);

  // Extract tasks from conclusions - only after reasoning completes
  const tasks = useMemo(() => {
    if (isEffectivelyLoading) return [];
    return extractTasksFromConclusions(effectiveConclusions, entries, effectiveMeta, taskDeps);
  }, [isEffectivelyLoading, effectiveConclusions, entries, effectiveMeta, taskDeps]);

  // Extract specific conclusion categories
  const readyTasks = useMemo(() => extractByPrefix(effectiveConclusions, "ready-"), [effectiveConclusions]);
  const completedTasks = useMemo(() => extractByPrefix(effectiveConclusions, "completed-"), [effectiveConclusions]);
  const blockedTasks = useMemo(() => extractByPrefix(effectiveConclusions, "blocked-"), [effectiveConclusions]);
  const assignments = useMemo(() => extractAssignments(effectiveConclusions), [effectiveConclusions]);

  // Query function
  const query = useCallback(
    (literal: string): "+D" | "+d" | "-D" | "-d" => {
      const found = effectiveConclusions.find((c) => c.literal === literal);
      return found?.status ?? "-d";
    },
    [effectiveConclusions]
  );

  // Manual refresh - bypasses cache
  const refresh = useCallback(() => {
    lastTheoryRef.current = ""; // Force re-run
    setCachedConclusions(null); // Clear cache to force reasoning
    setCachedMeta(null);
    setRefreshTrigger((n) => n + 1);
  }, []);

  return {
    isLoading: isEffectivelyLoading,
    loadingStage: cachedConclusions !== null ? "done" : worker.loadingStage,
    conclusions: effectiveConclusions,
    tasks,
    taskDeps,
    readyTasks,
    completedTasks,
    blockedTasks,
    assignments,
    query,
    refresh,
  };
}

/**
 * Convert SPL (S-expression) plan source to DFL format
 */
function splToDfl(spl: string): string {
  const lines: string[] = [];

  // Parse SPL forms and convert to DFL
  // This is a simplified converter - full conversion would require proper SPL parsing

  // Match (given ...) forms -> >> fact
  const givenRegex = /\(given\s+([a-zA-Z0-9_-]+)\)/g;
  let match;
  while ((match = givenRegex.exec(spl)) !== null) {
    lines.push(`>> ${match[1]}`);
  }

  // Match (normally label cond conclusion) -> label: cond => conclusion
  const normallyRegex = /\(normally\s+([a-zA-Z0-9_-]+)\s+\(and([^)]+)\)\s+([a-zA-Z0-9_-]+)\)/g;
  while ((match = normallyRegex.exec(spl)) !== null) {
    const label = match[1];
    const conditions = match[2].trim().split(/\s+/).filter(Boolean);
    const conclusion = match[3];
    lines.push(`${label}: ${conditions.join(", ")} => ${conclusion}`);
  }

  // Match (prefer r1 r2) -> r1 > r2
  const preferRegex = /\(prefer\s+([a-zA-Z0-9_-]+)\s+([a-zA-Z0-9_-]+)\)/g;
  while ((match = preferRegex.exec(spl)) !== null) {
    lines.push(`${match[1]} > ${match[2]}`);
  }

  // Match (except label cond (not conclusion)) -> defeater
  const exceptRegex = /\(except\s+([a-zA-Z0-9_-]+)\s+([a-zA-Z0-9_-]+)\s+\(not\s+([a-zA-Z0-9_-]+)\)\)/g;
  while ((match = exceptRegex.exec(spl)) !== null) {
    const label = match[1];
    const condition = match[2];
    const conclusion = match[3];
    lines.push(`${label}: ${condition} ~> -${conclusion}`);
  }

  return lines.join("\n");
}

/**
 * Convert a single SPL form to DFL
 */
function formToDfl(form: string): string | null {
  // (given X) -> >> X
  const givenMatch = form.match(/\(given\s+([a-zA-Z0-9_-]+)\)/);
  if (givenMatch) {
    return `>> ${givenMatch[1]}`;
  }

  // (not X) -> >> -X
  const notMatch = form.match(/\(not\s+([a-zA-Z0-9_-]+)\)/);
  if (notMatch) {
    return `>> -${notMatch[1]}`;
  }

  return null;
}

/**
 * Extract literals with a given prefix from positive conclusions
 */
function extractByPrefix(conclusions: Conclusion[], prefix: string): string[] {
  return conclusions
    .filter(
      (c) =>
        (c.status === "+D" || c.status === "+d") &&
        c.literal.startsWith(prefix)
    )
    .map((c) => c.literal.slice(prefix.length));
}

/**
 * Extract assignments from assign-* conclusions
 */
function extractAssignments(conclusions: Conclusion[]): Assignment[] {
  const assignments: Assignment[] = [];

  for (const c of conclusions) {
    if (c.status !== "+D" && c.status !== "+d") continue;

    // Match assign-<task>-<agent> pattern
    const match = c.literal.match(/^assign-([a-zA-Z0-9_-]+)-([a-zA-Z0-9_-]+)$/);
    if (match) {
      const task = match[1];
      const agent = match[2];
      let agentType: "C" | "S" | "A" | undefined;
      if (agent.includes("coder")) agentType = "C";
      else if (agent.includes("security")) agentType = "S";
      else if (agent.includes("architect")) agentType = "A";

      assignments.push({ task, agent, agentType });
    }
  }

  return assignments;
}

/**
 * Extract task metadata from (meta task-id ...) forms
 * Supports both :key "value" and (key "value") formats
 */
function extractTaskMeta(entries: LogEntry[]): Map<string, TaskMeta> {
  const metaMap = new Map<string, TaskMeta>();

  for (const entry of entries) {
    for (const form of entry.forms) {
      // Match (meta task-<id> ...) or (meta r-<rule-id> ...)
      const metaMatch = form.match(/\(meta\s+(?:task-|r-)?([a-zA-Z0-9_-]+)\s*/);
      if (metaMatch) {
        const taskId = metaMatch[1];
        const meta: TaskMeta = metaMap.get(taskId) || {};

        // Parse (key "value") format - e.g., (description "...")
        const parenKeyValues = form.matchAll(/\(([a-zA-Z_-]+)\s+"([^"]+)"\)/g);
        for (const match of parenKeyValues) {
          const key = match[1];
          const value = match[2];
          if (key === "description") meta.description = value;
          else if (key === "priority") meta.priority = value;
          else if (key === "estimate") meta.estimate = value;
          else if (key === "note") meta.note = value;
          else if (key === "source") meta.source = value;
          else if (key === "created-at") meta.createdAt = value;
          else if (key === "updated-at") meta.updatedAt = value;
          else meta[key] = value; // Store any additional fields
        }

        // Parse :key "value" format - e.g., :description "..."
        const colonKeyValues = form.matchAll(/:([a-zA-Z_-]+)\s+"([^"]+)"/g);
        for (const match of colonKeyValues) {
          const key = match[1];
          const value = match[2];
          if (key === "description") meta.description = value;
          else if (key === "priority") meta.priority = value;
          else if (key === "estimate") meta.estimate = value;
          else if (key === "note") meta.note = value;
          else if (key === "source") meta.source = value;
          else if (key === "created-at") meta.createdAt = value;
          else if (key === "updated-at") meta.updatedAt = value;
          else meta[key] = value;
        }

        // Parse :tags ("tag1" "tag2" ...) or (tags ("tag1" "tag2" ...))
        const tagsMatch = form.match(/(?::tags|\(tags)\s+\(([^)]+)\)/);
        if (tagsMatch) {
          meta.tags = tagsMatch[1].match(/"([^"]+)"/g)?.map(s => s.slice(1, -1)) || [];
        }

        metaMap.set(taskId, meta);
      }

      // Also extract notes from claims with :note
      const claimsNoteMatch = form.match(/\(claims[^)]*:note\s+"([^"]+)"[^)]*\(given\s+(blocked|claimed)-([a-zA-Z0-9_-]+)\)/);
      if (claimsNoteMatch) {
        const note = claimsNoteMatch[1];
        const taskId = claimsNoteMatch[3];
        const meta: TaskMeta = metaMap.get(taskId) || {};
        meta.note = note;
        metaMap.set(taskId, meta);
      }
    }
  }

  return metaMap;
}

/**
 * Extract task dependencies from WASM meta entries.
 * Looks for META entries with label "task-X" or "r-dep-X" that have a "depends" property.
 */
function extractTaskDepsFromMeta(wasmMeta: MetaEntry[]): Map<string, string[]> {
  const depMap = new Map<string, string[]>();

  for (const entry of wasmMeta) {
    // Check for "depends" property which should be an array of task IDs
    const depends = entry.properties["depends"];
    if (depends && Array.isArray(depends)) {
      // Extract task ID from label (could be "task-X" or "r-dep-X")
      let taskId = entry.label;
      if (taskId.startsWith("task-")) {
        taskId = taskId.slice(5);
      } else if (taskId.startsWith("r-dep-")) {
        taskId = taskId.slice(6);
      }
      depMap.set(taskId, depends);
    }
  }

  return depMap;
}

/**
 * Extract task dependencies from SPL source by parsing r-ready-* and r-dep-* rules.
 *
 * Uses the hence CLI approach: build a rule graph and trace prerequisites transitively.
 * This handles complex dependency patterns including phase-based dependencies.
 */
function extractTaskDepsFromSpl(spl: string): Map<string, string[]> {
  const depMap = new Map<string, string[]>();

  // Step 1: Extract all task IDs from task-* facts
  const taskIds = new Set<string>();
  const taskFactRegex = /\(given\s+task-([a-zA-Z0-9_-]+)\)/g;
  let taskMatch;
  while ((taskMatch = taskFactRegex.exec(spl)) !== null) {
    taskIds.add(taskMatch[1]);
  }

  // Step 2: Build rule graph - find all rules and their body literals
  // Maps head literal -> body literals
  const ruleGraph = new Map<string, string[]>();

  // Match normally rules: (normally label body head)
  // Use a balanced parenthesis approach to extract body content
  const normallyPattern = /\(normally\s+([a-zA-Z0-9_-]+)\s+/g;
  let ruleMatch;
  while ((ruleMatch = normallyPattern.exec(spl)) !== null) {
    const label = ruleMatch[1];
    const startIdx = ruleMatch.index + ruleMatch[0].length;

    // Extract body by finding balanced parentheses
    const bodyContent = extractBalancedContent(spl, startIdx);
    if (!bodyContent) continue;

    // Find the head (next non-whitespace after body)
    const afterBody = startIdx + bodyContent.fullLength;
    const headMatch = spl.slice(afterBody).match(/^\s*([a-zA-Z0-9_-]+)/);
    if (!headMatch) continue;

    const headLit = headMatch[1];
    const bodyLits = extractLiterals(bodyContent.content);

    // Add to rule graph
    const existing = ruleGraph.get(headLit) || [];
    for (const lit of bodyLits) {
      if (!existing.includes(lit)) existing.push(lit);
    }
    ruleGraph.set(headLit, existing);
  }

  // Step 3: Trace prerequisites transitively (like hence CLI)
  function tracePrerequisites(startLit: string, visited = new Set<string>()): Set<string> {
    if (visited.has(startLit)) return visited;
    visited.add(startLit);
    for (const prereq of ruleGraph.get(startLit) || []) {
      tracePrerequisites(prereq, visited);
    }
    return visited;
  }

  // Step 4: For each task, find its dependencies
  for (const taskId of taskIds) {
    const readyLit = `ready-${taskId}`;
    if (!ruleGraph.has(readyLit)) continue;

    const allPrereqs = tracePrerequisites(readyLit);
    const taskDeps: string[] = [];

    for (const prereq of allPrereqs) {
      if (prereq.startsWith("completed-")) {
        const depTask = prereq.slice(10);
        if (taskIds.has(depTask) && !taskDeps.includes(depTask)) {
          taskDeps.push(depTask);
        }
      }
    }

    if (taskDeps.length > 0) {
      depMap.set(taskId, taskDeps);
    }
  }

  return depMap;
}

/**
 * Extract content within balanced parentheses starting at the given index.
 * Returns the content and full length including parens.
 */
function extractBalancedContent(spl: string, startIdx: number): { content: string; fullLength: number } | null {
  if (spl[startIdx] !== "(") {
    // Not a paren-wrapped body, might be a simple literal
    const simpleMatch = spl.slice(startIdx).match(/^([a-zA-Z0-9_-]+)/);
    if (simpleMatch) {
      return { content: simpleMatch[1], fullLength: simpleMatch[1].length };
    }
    return null;
  }

  let depth = 1;
  let idx = startIdx + 1;
  while (idx < spl.length && depth > 0) {
    if (spl[idx] === "(") depth++;
    else if (spl[idx] === ")") depth--;
    idx++;
  }

  if (depth !== 0) return null;

  return {
    content: spl.slice(startIdx + 1, idx - 1),
    fullLength: idx - startIdx,
  };
}

/**
 * Extract all literals from a rule body content.
 * Handles (and lit1 lit2 ...) format and simple literals.
 */
function extractLiterals(body: string): string[] {
  const literals: string[] = [];
  const trimmed = body.trim();

  // Check if it's an (and ...) construct
  if (trimmed.startsWith("and ")) {
    const content = trimmed.slice(4);
    const litRegex = /([a-zA-Z][a-zA-Z0-9_-]*)/g;
    let match;
    while ((match = litRegex.exec(content)) !== null) {
      if (match[1] !== "and" && match[1] !== "or" && match[1] !== "not") {
        literals.push(match[1]);
      }
    }
  } else {
    // Simple literal or other structure
    const litRegex = /([a-zA-Z][a-zA-Z0-9_-]*)/g;
    let match;
    while ((match = litRegex.exec(trimmed)) !== null) {
      literals.push(match[1]);
    }
  }

  return literals;
}

/**
 * Convert WASM meta entries to TaskMeta map
 * Keys are task IDs (without "task-" prefix)
 */
function wasmMetaToMap(wasmMeta: MetaEntry[]): Map<string, TaskMeta> {
  const metaMap = new Map<string, TaskMeta>();

  for (const entry of wasmMeta) {
    // Extract task ID from label (strip "task-" prefix if present)
    let taskId = entry.label;
    if (taskId.startsWith("task-")) {
      taskId = taskId.slice(5);
    }

    const meta: TaskMeta = metaMap.get(taskId) || {};

    for (const [key, value] of Object.entries(entry.properties)) {
      // Map known keys
      if (key === "description") meta.description = value as string;
      else if (key === "priority") meta.priority = value as string;
      else if (key === "estimate") meta.estimate = value as string;
      else if (key === "note") meta.note = value as string;
      else if (key === "source") meta.source = value as string;
      else if (key === "created-at") meta.createdAt = value as string;
      else if (key === "updated-at") meta.updatedAt = value as string;
      else if (key === "tags") meta.tags = value as string[];
      else meta[key] = value; // Store any additional fields
    }

    metaMap.set(taskId, meta);
  }

  return metaMap;
}

/**
 * Build index mapping fact literals to their source log entries.
 * For facts asserted via (given X), maps "X" to the entry.
 */
function buildFactSourceIndex(entries: LogEntry[]): Map<string, LogEntry> {
  const index = new Map<string, LogEntry>();

  for (const entry of entries) {
    for (const form of entry.forms) {
      // Match (given X) facts
      const givenMatch = form.match(/\(given\s+([a-zA-Z0-9_-]+)\)/g);
      if (givenMatch) {
        for (const match of givenMatch) {
          const factMatch = match.match(/\(given\s+([a-zA-Z0-9_-]+)\)/);
          if (factMatch) {
            index.set(factMatch[1], entry);
          }
        }
      }
    }
  }

  return index;
}

/**
 * Build task list from conclusions and entries
 */
function extractTasksFromConclusions(
  conclusions: Conclusion[],
  entries: LogEntry[],
  wasmMeta: MetaEntry[] = [],
  taskDeps: Map<string, string[]> = new Map()
): Task[] {
  const tasks = new Map<string, Task>();
  const concluded = new Map<string, Conclusion>();

  // Use WASM meta for task metadata
  const taskMeta = wasmMeta.length > 0
    ? wasmMetaToMap(wasmMeta)
    : extractTaskMeta(entries);

  // Build index of fact sources to find who claimed tasks
  const factSource = buildFactSourceIndex(entries);

  // Index conclusions by literal - prefer positive conclusions (+D, +d) over negative (-D, -d)
  for (const c of conclusions) {
    const existing = concluded.get(c.literal);
    if (!existing) {
      concluded.set(c.literal, c);
    } else {
      // Keep the positive one if we have both
      const isPositive = c.status === "+D" || c.status === "+d";
      const existingIsPositive = existing.status === "+D" || existing.status === "+d";
      if (isPositive && !existingIsPositive) {
        concluded.set(c.literal, c);
      }
    }
  }

  // First pass: find all task-* declarations from entries
  for (const entry of entries) {
    for (const form of entry.forms) {
      const taskMatch = form.match(/\(given task-([a-zA-Z0-9_-]+)\)/);
      if (taskMatch) {
        const id = taskMatch[1];
        if (!tasks.has(id)) {
          tasks.set(id, { id, status: "backlog", meta: taskMeta.get(id) });
        }
      }
    }
  }

  // Also check conclusions for task-* facts
  for (const c of conclusions) {
    if ((c.status === "+D" || c.status === "+d") && c.literal.startsWith("task-")) {
      const id = c.literal.slice(5);
      if (!tasks.has(id)) {
        tasks.set(id, { id, status: "backlog", meta: taskMeta.get(id) });
      }
    }
  }

  // Second pass: update statuses based on conclusions
  for (const [id, task] of tasks) {
    // Attach meta if not already attached
    if (!task.meta && taskMeta.has(id)) {
      task.meta = taskMeta.get(id);
    }

    // Check completed
    const completedConc = concluded.get(`completed-${id}`);
    if (completedConc && (completedConc.status === "+D" || completedConc.status === "+d")) {
      task.status = "done";
      task.conclusion = completedConc.status;
      // Get agent from the log entry that asserted completed-{id}
      const completedEntry = factSource.get(`completed-${id}`);
      if (completedEntry) {
        task.agent = completedEntry.agent;
        if (completedEntry.sig?.startsWith("ed25519:")) {
          task.agentKeyHash = completedEntry.sig;
        }
      }
      continue;
    }

    // Check blocked
    const blockedConc = concluded.get(`blocked-${id}`);
    if (blockedConc && (blockedConc.status === "+D" || blockedConc.status === "+d")) {
      task.status = "blocked";
      task.conclusion = blockedConc.status;
      // Get note from meta if available
      if (task.meta?.note) {
        task.note = task.meta.note;
      }
      continue;
    }

    // Check claimed (in progress) - but not if unclaimed
    const claimedConc = concluded.get(`claimed-${id}`);
    const unclaimedConc = concluded.get(`unclaimed-${id}`);
    const isClaimed = claimedConc && (claimedConc.status === "+D" || claimedConc.status === "+d");
    const isUnclaimed = unclaimedConc && (unclaimedConc.status === "+D" || unclaimedConc.status === "+d");

    if (isClaimed && !isUnclaimed) {
      task.status = "in_progress";

      // Get agent from the log entry that asserted claimed-{id}
      const claimEntry = factSource.get(`claimed-${id}`);
      if (claimEntry) {
        task.agent = claimEntry.agent;
        // Use sig for avatar (ed25519 key hash)
        if (claimEntry.sig?.startsWith("ed25519:")) {
          task.agentKeyHash = claimEntry.sig;
        }
      }
      continue;
    }

    // Check ready
    const readyConc = concluded.get(`ready-${id}`);
    if (readyConc && (readyConc.status === "+D" || readyConc.status === "+d")) {
      task.status = "ready";
      task.conclusion = readyConc.status;
      continue;
    }

    // Task is in backlog - find incomplete dependencies
    const deps = taskDeps.get(id);
    if (deps && deps.length > 0) {
      const incompleteDeps = deps.filter((depId) => {
        const completedConc = concluded.get(`completed-${depId}`);
        return !(completedConc && (completedConc.status === "+D" || completedConc.status === "+d"));
      });
      if (incompleteDeps.length > 0) {
        task.blockedBy = incompleteDeps;
      }
    }
  }

  return Array.from(tasks.values()).sort((a, b) => a.id.localeCompare(b.id));
}

export type { Task, TaskMeta, Assignment, PlanReasoningResult };
